import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Quote } from 'lucide-react';
import { getAvatarUrl } from '../../utils/avatar';

const DashboardHeader = ({ user }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatar]);

  const quotes = [
    "Precision in care, excellence in outcomes.",
    "Empowering healthcare through seamless technology.",
    "Your dedication saves lives, our system simplifies the journey.",
    "Focus on healing, we'll handle the coordination.",
    "Data-driven insights for world-class surgical management.",
    "Efficiency is the foundation of patient satisfaction.",
    "The future of surgery is connected and transparent."
  ];

  const [quote, setQuote] = useState(quotes[0]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    setQuote(quotes[Math.floor(Math.random() * quotes.length)]);
    
    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();

    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";

    return "Good Evening";
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="relative overflow-hidden bg-white rounded-[24px] sm:rounded-[32px] md:rounded-[48px] p-5 sm:p-8 md:p-10 border border-gray-100 shadow-xl shadow-gray-200/40 mb-6 sm:mb-10 group">
      
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-blue-50 rounded-full -mr-32 -mt-32 md:-mr-48 md:-mt-48 blur-3xl opacity-40 group-hover:opacity-60 transition-opacity"></div>

      <div className="absolute bottom-0 left-0 w-48 md:w-64 h-48 md:h-64 bg-emerald-50 rounded-full -ml-24 -mb-24 md:-ml-32 md:-mb-32 blur-3xl opacity-30 group-hover:opacity-50 transition-opacity"></div>

      <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6 md:gap-10">
        
        <div className="space-y-4 md:space-y-6 flex-1 min-w-0">
          
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-[#143E78] tracking-tighter leading-tight break-words">
              {getGreeting()},{" "}
              
              <span className="text-blue-500 block sm:inline">
                {user?.role === 'doctor'
                  ? `Dr. ${user?.firstName} ${user?.lastName}`
                  : user?.firstName}
              </span>
            </h2>

            <div className="flex flex-wrap items-center gap-2 mt-3 md:mt-4">
              
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-xl border border-blue-100 shadow-sm">
                <Calendar size={12} className="shrink-0" />

                <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
                  {formatDate(currentTime)}
                </span>
              </div>

              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 text-slate-600 rounded-xl border border-slate-100 shadow-sm">
                <Clock size={12} className="shrink-0" />

                <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
                  {formatTime(currentTime)}
                </span>
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-start gap-3 max-w-2xl bg-gray-50/50 p-4 md:p-0 md:bg-transparent rounded-2xl border border-gray-100 md:border-none shadow-inner md:shadow-none">
            <Quote size={18} className="text-blue-300 shrink-0 mt-1 opacity-50" />

            <p className="text-gray-500 font-medium italic text-sm md:text-lg leading-relaxed break-words">
              "{quote}"
            </p>
          </div>
        </div>

        {/* Right Side Analytics */}
        <div className="hidden md:flex items-center justify-between lg:justify-end gap-6 pt-5 md:pt-0 border-t lg:border-none border-gray-100 w-full lg:w-auto">
           <div className="w-12 h-12 md:w-16 md:h-16 bg-[#143E78] rounded-2xl md:rounded-3xl flex items-center justify-center text-white shadow-2xl shadow-blue-900/30 transform hover:rotate-6 transition-all border-4 border-white/50 ring-1 ring-blue-100 ml-auto lg:ml-0 overflow-hidden">
               {user?.avatar && !avatarError ? (
                 <img 
                   src={getAvatarUrl(user.avatar)} 
                   alt="" 
                   className="w-full h-full object-cover" 
                   onError={() => setAvatarError(true)}
                 />
               ) : (
                 <span className="text-xl md:text-2xl font-black">
                   {user?.firstName?.[0] || 'A'}{user?.lastName?.[0] || 'U'}
                 </span>
               )}
           </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;